import { cloneDeep, cloneDeepWith } from 'lodash-es';

import { Task } from '@tasks/tasks.types';
import { TaskUtils } from '@tasks/tasks.utils';

/**
 * State slice required to compute the patch after a task update.
 * Avoids importing TaskStateModel from state.ts (no circular dependency).
 */
export type TaskUpdateStateSlice = {
  tasks: Task[];
  currentTask: Task | null;
};

/**
 * Patch returned by computeTaskUpdatePatch. Can be passed to NgXS patchState
 * to update TaskStateModel (tasks and optionally currentTask).
 */
export type TaskUpdatePatch = {
  tasks: Task[];
  currentTask?: Task;
};

/**
 * Pure function: given current state slice and the task returned by the API,
 * returns a patch for TaskStateModel (tasks array with task merged in tree,
 * and currentTask if the updated task is current or a child of current).
 * Does not mutate apiTask or stateSlice.
 */
export function computeTaskUpdatePatch(stateSlice: TaskUpdateStateSlice, apiTask: Task): TaskUpdatePatch {
  const currentTask = stateSlice.currentTask;

  const normalizedTask: Task = {
    ...apiTask,
    children: apiTask.children ? TaskUtils.deepSortTasksByRank(cloneDeep(apiTask.children)) : apiTask.children,
    todos: apiTask.todos?.length ? [...apiTask.todos].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)) : apiTask.todos,
  };

  const newTasksArray = cloneDeepWith(stateSlice.tasks, (value) => {
    if (value?.id === apiTask.id && value?.task_type === apiTask.task_type) {
      return { ...value, ...normalizedTask };
    }
  });

  const patch: TaskUpdatePatch = {
    tasks: newTasksArray,
  };

  if (currentTask && normalizedTask.parent_id === currentTask.id) {
    const updatedCurrentTask = {
      ...currentTask,
      children: TaskUtils.deepUpdateTaskById(currentTask.children ?? [], normalizedTask),
    };
    patch.currentTask = updatedCurrentTask;
  }

  if (currentTask && normalizedTask.id === currentTask.id) {
    patch.currentTask = normalizedTask;
  }

  return patch;
}
